/**
 * SaveTemplateDialog
 * Prompts the trainer for a template name (and optional notes) then calls
 * sablona.saveFromMeal to persist the current meal category as a new template.
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save } from "lucide-react";
import type { Prijem } from "../types";

interface SaveTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The current intake items to save as a template */
  items: Prijem[];
  /** Suggested default name (e.g. the meal category name) */
  defaultName?: string;
  /** Called after the template has been saved successfully */
  onSaved?: () => void;
}

export function SaveTemplateDialog({
  open,
  onOpenChange,
  items,
  defaultName = "",
  onSaved,
}: SaveTemplateDialogProps) {
  const { t } = useTranslation();
  const [nazov, setNazov] = useState(defaultName);
  const [poznamka, setPoznamka] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset fields when dialog opens with a new defaultName
  const handleOpenChange = (v: boolean) => {
    if (v) {
      setNazov(defaultName);
      setPoznamka("");
    }
    onOpenChange(v);
  };

  const saveMutation = trpc.sablona.saveFromMeal.useMutation();
  const utils = trpc.useUtils();

  // Only food-backed rows can be saved as template items (recipes are excluded)
  const savableItems = items.filter(
    i => i.potravinaId != null && i.aktivne !== false
  );

  const handleSave = async () => {
    if (!nazov.trim()) return;
    setSaving(true);
    try {
      await saveMutation.mutateAsync({
        nazov: nazov.trim(),
        poznamka: poznamka.trim() || undefined,
        items: savableItems.map(i => ({
          potravinaId: i.potravinaId!,
          hmotnost: i.hmotnost,
        })),
      });
      toast.success(t("mealLibrary:sablona.savedFromMeal", "Template saved"));
      utils.sablona.list.invalidate();
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error(
        t("mealLibrary:sablona.saveFromMealError", "Failed to save template")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Save className="h-5 w-5" />
            {t("mealLibrary:sablona.saveAsTemplate", "Save as Template")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="template-name">
              {t("mealLibrary:sablona.templateName", "Template name")}
              <span className="text-destructive ml-0.5">*</span>
            </Label>
            <Input
              id="template-name"
              value={nazov}
              onChange={e => setNazov(e.target.value)}
              placeholder={t(
                "mealLibrary:sablona.templateNamePlaceholder",
                "e.g. Yogurt Breakfast"
              )}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="template-notes">
              {t("mealLibrary:sablona.notes", "Notes")}
              <span className="text-muted-foreground text-xs ml-1">
                ({t("common:optional", "optional")})
              </span>
            </Label>
            <Textarea
              id="template-notes"
              value={poznamka}
              onChange={e => setPoznamka(e.target.value)}
              rows={2}
              placeholder={t(
                "mealLibrary:sablona.notesPlaceholder",
                "Preparation notes…"
              )}
            />
          </div>

          {/* Preview of items to be saved (recipes and inactive alternatives excluded) */}
          {savableItems.length > 0 && (
            <div className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">
                {savableItems.length}{" "}
                {t("mealLibrary:sablona.itemsToSave", "items will be saved")}:
              </span>{" "}
              {savableItems.map(i => i.potravina.nazov).join(", ")}
              {items.length > savableItems.length && (
                <span className="ml-1 italic">
                  ({items.length - savableItems.length} recipe/inactive items
                  excluded)
                </span>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common:cancel", "Cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!nazov.trim() || saving || savableItems.length === 0}
          >
            {saving
              ? t("mealLibrary:sablona.saving", "Saving…")
              : t("mealLibrary:sablona.save", "Save Template")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
